import { memo, useState } from "react";
import StatCardRow from "../../shared/StatCardRow";
import DrillModal from "../../shared/DrillModal";
import RecordModal from "../../shared/RecordModal";
import DashboardTable from "../../shared/DashboardTable";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useMountFlash } from "../../hooks/useMountFlash";
import {
  fetchReportItems,
  fetchReprimands,
} from "../../services/dashboardApi";
import { reportItemDrillColumns, withId } from "../../utils/drillHelpers";
import { formatDate } from "../../utils/formatters";
import "../../reportDashboard.scss";

// Risk score bands mirror the same danger/warning/corrective tone scale used elsewhere.
const riskTone = (score) =>
  score >= 6 ? "danger" : score >= 3 ? "warning" : "corrective";

const subcontractorColumns = [
  // Rank is positional (assigned by the caller before rows reach the table),
  // not a DB field — matches HTML's `${i+1}` scorecard row index (line 2966).
  { field: "rank", headerName: "#", width: 50, sortable: false },
  // Subcontractor company names can run considerably longer than a typical
  // person/asset name (e.g. "Southeastern Concrete & Masonry Contractors LLC").
  { field: "name", headerName: "Subcontractor", flex: 1, minWidth: 140 },
  {
    field: "correctiveCount",
    headerName: "Correctives",
    width: 110,
    type: "number",
  },
  {
    field: "reprimandCount",
    headerName: "Reprimands",
    width: 110,
    type: "number",
  },
  {
    field: "kudosCount",
    headerName: "Kudos",
    width: 90,
    type: "number",
  },
  {
    field: "riskScore",
    headerName: "Risk Score",
    width: 110,
    renderCell: (params) => (
      <span className={`rdRiskTag is-${riskTone(params.value)}`}>
        {params.value}
      </span>
    ),
  },
];

const reprimandColumns = [
  { field: "reprimandId", headerName: "ID", width: 90 },
  {
    field: "dateOfEntry",
    headerName: "Date",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
  { field: "involvedParty", headerName: "Party", flex: 1, minWidth: 120 },
  { field: "jobSite", headerName: "Job Site", flex: 1, minWidth: 120 },
  { field: "inspectedBy", headerName: "Inspected By", flex: 1, minWidth: 120 },
  { field: "status", headerName: "Status", width: 100 },
];

// `involvedUserType` is a 3-value category code from the real backend
// `InspectionInvolvedPartyType` enum: Employee = 0, SubContractor = 1,
// NotAvailable = 2. Attribution means "this corrective item was attributed
// to a subcontractor specifically", so we check for === 1 (SubContractor).
const SubcontractorsSection = ({ data, apiCtx }) => {
  const { attribution, subcontractors = [] } = data;

  const [drill, setDrill] = useState(null);
  const [reprimandDetail, setReprimandDetail] = useState(null);

  // Page-payload-fed tables below have no fetch of their own to report; this
  // gives them a brief loading state when the section mounts (tab switch). A
  // page-payload refetch (date-range change) is deliberately NOT included: the
  // shell shows one centred spinner for that, so a section adding its own here
  // would put a second loader on screen for a single click.
  const mountFlash = useMountFlash();
  const tableLoading = mountFlash;

  const reprimandList = usePaginatedList(
    (params) => fetchReprimands(apiCtx, params),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );

  // Same shape as InspectionsSection's openDrill: DrillModal fetches its own
  // pages now, so this just records the filter rather than eagerly fetching one
  // 200-row batch up front.
  //
  // Drills the findings log (GetReportItemsList) rather than GetCorrectiveItemsList.
  // The scorecard's Corrective count comes off that log, so drilling the old
  // InsnCorrective-backed endpoint opened a list that disagreed with the number
  // clicked (41 vs 11 for the largest live subcontractor).
  //
  // Filtered by involvedPartyId, not name: several real subcontractors have a NULL
  // Users.LastName, so a `FirstName + ' ' + LastName` equality match against the
  // displayed name returns nothing for them under NULL-propagating concat. Matching
  // on the id also stops two subcontractors sharing a full name from drilling into
  // each other's items.
  const openSubDrill = (row) => {
    setDrill({
      title: `Subcontractor: ${row.name}`,
      filter: { itemType: "Corrective", involvedPartyId: row.userId },
    });
  };

  // Subcontractor Kudos is a sum of each row's own kudosCount (SubcontractorDto),
  // not a separate item count — matches HTML's stat card (renderSubcontractors
  // line 2996: subKudos.length, which the scorecard rows arrive at the same way).
  const subcontractorKudos = subcontractors.reduce((sum, s) => sum + (s.kudosCount || 0), 0);

  // Drawn on two stacked layers in the attribution bar, so it's named once here
  // rather than kept in sync between them.
  const attributionLabel = `${attribution.attributionRate}% attributed to a subcontractor`;

  return (
    <div>
      <StatCardRow
        items={[
          {
            label: "Subcontractors with Correctives",
            value: subcontractors.length,
            tone: "danger",
          },
          {
            // The reprimand log fetched here (fetchReprimands) is used only on this
            // tab, so its total already represents subcontractor-attributed
            // reprimands for this section's purposes.
            label: "Subcontractor Reprimands",
            value: reprimandList.totalCount,
            tone: "warning",
          },
          {
            label: "Subcontractor Kudos",
            value: subcontractorKudos,
            tone: "kudos",
          },
        ]}
        columns={3}
      />

      <div className="rdSectionLabel">Subcontractor Attribution Rate</div>
      <div className="rdCard">
        <div className="rdCard__title">
          How many corrective items are attributed to a subcontractor?
        </div>
        {/* Caption is an overlay on the bar rather than a flex child of the
            fill: it starts at the left edge inside the fill at any rate and
            runs on past it when the fill is short, instead of being clipped to
            the fill's width. Rendered twice (white copy clipped to the fill,
            dark copy under it) so it stays legible over both halves — see
            .rdBar__overlay. --rd-fill sits on the bar so both layers see it. */}
        <div
          className="rdBar rdBar--tall"
          style={{ "--rd-fill": `${attribution.attributionRate}%` }}
        >
          <div className="rdBar__fill is-accent" />
          <span className="rdBar__overlay">{attributionLabel}</span>
          <span className="rdBar__overlay rdBar__overlay--onFill" aria-hidden="true">
            {attributionLabel}
          </span>
        </div>
      </div>

      <div className="rdGrid rdGrid--2">
        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">
            <span>Subcontractor Risk Scorecard</span>
            {subcontractors.length > 0 && (
              <span className="rdCard__note">
                Corrective×1 + Reprimand×3 − Kudos×1 · click to drill
              </span>
            )}
          </div>
          <DashboardTable
            columns={subcontractorColumns}
            rows={subcontractors.map((s, i) => ({ ...s, rank: i + 1 }))}
            getRowId={(r) => r.name}
            onRowClick={(params) => openSubDrill(params.row)}
            loading={tableLoading}
          />
        </div>
        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">
            <span>Subcontractor Reprimand Log</span>
            {reprimandList.totalCount > 0 && (
              <span className="rdCard__note">click for details</span>
            )}
          </div>
          <DashboardTable
            columns={reprimandColumns}
            rows={withId(reprimandList.rows, "reprimandId")}
            onRowClick={(params) => setReprimandDetail(params.row)}
            serverMode
            rowCount={reprimandList.totalCount}
            page={reprimandList.page}
            onPageChange={reprimandList.setPage}
            loading={reprimandList.loading}
            pageSize={reprimandList.pageSize}
          />
        </div>
      </div>

      {drill && (
        <DrillModal
          open
          onClose={() => setDrill(null)}
          title={drill.title}
          columns={reportItemDrillColumns}
          idField="reportLogId"
          fetchPage={(p) => fetchReportItems(apiCtx, { ...drill.filter, ...p })}
          deps={[apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to, JSON.stringify(drill.filter)]}
        />
      )}

      {reprimandDetail && (
        <RecordModal
          open
          onClose={() => setReprimandDetail(null)}
          title="Reprimand Detail"
          fields={[
            { label: "Reprimand ID", value: reprimandDetail.reprimandId },
            { label: "Status", value: reprimandDetail.status },
            { label: "Date", value: formatDate(reprimandDetail.dateOfEntry) },
            { label: "Inspected By", value: reprimandDetail.inspectedBy },
            { label: "Job Site", value: reprimandDetail.jobSite },
            { label: "Involved Party", value: reprimandDetail.involvedParty },
          ]}
        />
      )}
    </div>
  );
};

export default memo(SubcontractorsSection);
